import React, { useMemo, useState } from "react";
import "./LeaguesPage.css";
import logo from "../../assets/logo.webp";
import SubscribeForm from "../../Components/SubscribeForm/SubscribeForm";
import SEO from "../../Components/SEO/SEO";
import { useContent } from "../../utils/useContent";

/* ---------- Fallback leagues ----------
 * Leagues are managed in the admin (Dashboard → Leagues) and served by
 * GET /content/leagues. This list only renders if the API is unreachable.
 */

const FALLBACK_LEAGUES = [
  {
    id: "prem",
    label: "Prem",
    page_url:
      "https://www.sofascore.com/football/tournament/canada/all-star-xperience-prem/34682#id:94195",
    widgets: [
      {
        id: "fixtures",
        title: "Fixtures & Results",
        src: "https://widgets.sofascore.com/embed/unique-tournament/34682/season/94195/editorFixtures?showCompetitionLogo=true&widgetTheme=light",
        height: 800,
        max_width: 620,
      },
      {
        id: "standings",
        title: "Regular Season 2026 Standings",
        src: "https://widgets.sofascore.com/embed/tournament/184005/season/94195/standings/Regular%20season%202026?widgetTitle=Regular%20season%202026&showCompetitionLogo=true",
        height: 723,
        max_width: 768,
        credit_url:
          "https://www.sofascore.com/football/tournament/canada/regular-season/34682#id:94195",
      },
    ],
  },
  {
    id: "division-a",
    label: "Div. A",
    page_url:
      "https://www.sofascore.com/football/tournament/canada/all-star-xperience-division-a/34680",
    widgets: [
      {
        id: "fixtures",
        title: "Fixtures & Results",
        src: "https://widgets.sofascore.com/embed/unique-tournament/34680/season/94193/editorFixtures?showCompetitionLogo=true&widgetTheme=light",
        height: 800,
        max_width: 620,
      },
      {
        id: "standings",
        title: "Regular Season 2026 Standings",
        src: "https://widgets.sofascore.com/embed/tournament/183999/season/94193/standings/Regular%20season%202026?widgetTitle=Regular%20season%202026&showCompetitionLogo=true",
        height: 723,
        max_width: 768,
        credit_url:
          "https://www.sofascore.com/football/tournament/canada/regular-season/34680#id:94193",
      },
    ],
  },
  {
    id: "division-b",
    label: "Div. B",
    page_url:
      "https://www.sofascore.com/football/tournament/canada/all-star-xperience-division-b/34681#id:94194",
    widgets: [
      {
        id: "fixtures",
        title: "Fixtures & Results",
        src: "https://widgets.sofascore.com/embed/unique-tournament/34681/season/94194/editorFixtures?showCompetitionLogo=true&widgetTheme=light",
        height: 800,
        max_width: 620,
      },
      {
        id: "standings",
        title: "Regular Season 2026 Standings",
        src: "https://widgets.sofascore.com/embed/tournament/184003/season/94194/standings/Regular%20season%202026?widgetTitle=Regular%20season%202026&showCompetitionLogo=true",
        height: 563,
        max_width: 768,
        credit_url:
          "https://www.sofascore.com/football/tournament/canada/regular-season/34681#id:94194",
      },
    ],
  },
];

export default function LeaguesPage() {
  const { items: leagues, loading } = useContent("leagues", FALLBACK_LEAGUES);
  const [activeLeagueId, setActiveLeagueId] = useState(null);

  const activeLeague = useMemo(() => {
    return (
      leagues.find((league) => String(league.id) === activeLeagueId) ||
      leagues[0]
    );
  }, [leagues, activeLeagueId]);

  return (
    <>
      <SEO
        title="Leagues & Standings | All Star Xperience Montreal"
        description="Live fixtures, results, and standings for ASX Montreal 7v7 soccer leagues — Prem, competitive men's, women's, and co-ed divisions."
        keywords="ASX leagues, Montreal soccer standings, 7v7 fixtures Montreal, All Star Xperience Prem"
      />
      <main className="leagues-root">
        <div className="container">
          <header className="leagues-header">
            <div>
              <h1 className="leagues-h1">Montreal 7v7 Soccer Leagues</h1>
              <p className="leagues-subtitle">
                Live fixtures, results, and standings for every All Star Xperience Montreal soccer league — Prem, competitive men's, women's, and co-ed divisions. Updated in real time from SofaScore.
              </p>
            </div>

            {!loading && (
              <div
                className="league-switcher"
                role="tablist"
                aria-label="Select league"
              >
                {leagues.map((league) => {
                  const isActive = league.id === activeLeague.id;

                  return (
                    <button
                      key={league.id}
                      type="button"
                      className={`league-switcher-btn ${
                        isActive ? "league-switcher-btn--active" : ""
                      }`}
                      onClick={() => setActiveLeagueId(String(league.id))}
                      aria-pressed={isActive}
                    >
                      {league.label}
                    </button>
                  );
                })}
              </div>
            )}
          </header>

          {/* Wait for the API before mounting iframes so the fallback
              widgets don't start loading and then get swapped out. */}
          {!loading && activeLeague && (
            <>
              <div className="active-league-banner">
                <span className="active-league-eyebrow">Currently viewing</span>
                <h2 className="active-league-title">{activeLeague.label}</h2>
              </div>

              <section className="lg-section sofascore-section">
                <div className="sofascore-widgets-grid">
                  {activeLeague.widgets.map((widget) => (
                    <SofaScoreWidgetCard
                      key={`${activeLeague.id}-${widget.id}`}
                      widget={widget}
                      league={activeLeague}
                    />
                  ))}
                </div>
              </section>
            </>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}

function SofaScoreWidgetCard({ widget, league }) {
  return (
    <article
      className={`lg-card sofascore-widget-card sofascore-widget-card--${widget.id}`}
    >
      <div className="sofascore-widget-head">
        <div>
          <h3 className="sofascore-widget-title">{widget.title}</h3>
          <p className="sofascore-widget-subtitle">
            Updated directly from SofaScore.
          </p>
        </div>

        {league.page_url && (
          <a
            className="sofascore-open-link"
            href={league.page_url}
            target="_blank"
            rel="noreferrer"
          >
            Open
          </a>
        )}
      </div>

      <div className="sofascore-frame-shell">
        <iframe
          id={`sofa-${league.id}-${widget.id}-embed`}
          title={`${league.label} ${widget.title}`}
          src={widget.src}
          className="sofascore-frame"
          style={{
            height: `${widget.height}px`,
            maxWidth: widget.max_width ? `${widget.max_width}px` : undefined,
          }}
          frameBorder="0"
          scrolling="no"
          loading="lazy"
        />
      </div>

      {(widget.credit_url || league.page_url) && (
        <div className="sofascore-credit">
          {widget.title} provided by{" "}
          <a
            href={widget.credit_url || league.page_url}
            target="_blank"
            rel="noreferrer"
          >
            SofaScore
          </a>
        </div>
      )}
    </article>
  );
}

/* ---------------- Footer ---------------- */

function Footer() {
  const year = new Date().getFullYear();

  const links = [
    { label: "Home", href: "/" },
    { label: "Leagues", href: "/leagues" },
    { label: "Hall of Fame", href: "/hall-of-fame" },
    { label: "Rules", href: "/rules" },
    { label: "About", href: "/about" },
    { label: "Register", href: "/register" },
  ];

  return (
    <footer className="site-footer" role="contentinfo">
      <div className="footer-top container">
        <div className="ft-col ft-brand">
          <div className="ft-logo">
            <img
              src={logo}
              alt="All Star Xperience"
              width="240"
              height="240"
              className="ft-logo-img"
            />
          </div>

          <div className="ft-social">
            <a
              className="ft-social-btn"
              href="https://www.instagram.com/allstarxperience/#"
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
              title="Instagram"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7Zm5 3.5A5.5 5.5 0 1 1 6.5 13 5.5 5.5 0 0 1 12 7.5Zm0 2A3.5 3.5 0 1 0 15.5 13 3.5 3.5 0 0 0 12 9.5Zm6-2.25a1.25 1.25 0 1 1-1.25 1.25A1.25 1.25 0 0 1 18 7.25Z"
                />
              </svg>
              <span>Instagram</span>
            </a>

            <a
              className="ft-social-btn"
              href="https://www.facebook.com/allstarxperience"
              target="_blank"
              rel="noreferrer"
              aria-label="Facebook"
              title="Facebook"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M13.5 9H16l.5-3h-3V4.5c0-.8.2-1.5 1.5-1.5H16V0h-2c-2.5 0-4 1.5-4 4.2V6H8v3h2v9h3.5V9Z"
                />
              </svg>
              <span>Facebook</span>
            </a>
          </div>
        </div>

        <div className="ft-col" id="contact">
          <h4 className="ft-title">Contact</h4>
          <ul className="ft-contact">
            <li>
              <span className="ft-k">Email</span>
              <a href="mailto:allstarxperience@gmail.com">
                allstarxperience@gmail.com
              </a>
            </li>
            <li>
              <span className="ft-k">Phone</span>
              <a href="tel:+14387283726">+1 (438) 728-3726</a>
            </li>
            <li>
              <span className="ft-k">Phone</span>
              <a href="tel:+14385089880">+1 (438) 508-9880</a>
            </li>
            <li>
              <span className="ft-k">City</span>
              <span>Montreal, QC</span>
            </li>
          </ul>
        </div>

        <div className="ft-col explore-col">
          <h4 className="ft-title explore-title">Explore</h4>
          <ul className="ft-links">
            {links.map((l) => (
              <li key={l.label}>
                <a href={l.href}>{l.label}</a>
              </li>
            ))}
          </ul>
        </div>

        <div className="ft-col">
          <h4 className="ft-title">Stay in the Loop</h4>
          <p className="ft-note">News, fixtures, and registration reminders.</p>

          <SubscribeForm />
        </div>
      </div>

      <div className="footer-bottom">
        <div className="container footer-bottom-inner">
          <span>
            © {year} All Star Xperience. All rights reserved. Developed by
            Riachiッ.
          </span>

          <div className="ft-legal-links">
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
            <a href="#contact">Contact</a>
          </div>
        </div>
      </div>
    </footer>
  );
}